// frontend/src/components/chatbot/ChatbotContext.jsx
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const CHATBOT_STORAGE_KEY = 'pista_chatbot_session_id';

const ChatbotContext = createContext(null);

export const ChatbotProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try {
      return localStorage.getItem(CHATBOT_STORAGE_KEY) || uuidv4();
    } catch {
      return uuidv4();
    }
  });

  // Persist sessionId
  useEffect(() => {
    try {
      localStorage.setItem(CHATBOT_STORAGE_KEY, sessionId);
    } catch {
      // Ignore storage errors
    }
  }, [sessionId]);

  const toggleOpen = useCallback(() => setIsOpen(prev => !prev), []);
  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);

  const addMessage = useCallback((message) => {
    setMessages(prev => [...prev, message]);
  }, []);

  const updateLastMessage = useCallback((updater) => {
    setMessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const updated = typeof updater === 'function' ? updater(last) : updater;
      return [...prev.slice(0, -1), updated];
    });
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    // Rotate session ID on clear for privacy
    setSessionId(uuidv4());
  }, []);

  const value = {
    isOpen,
    toggleOpen,
    openChat,
    closeChat,
    messages,
    addMessage,
    updateLastMessage,
    clearMessages,
    isLoading,
    setIsLoading,
    sessionId,
  };

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  );
};

export const useChatbotContext = () => {
  const ctx = useContext(ChatbotContext);
  if (!ctx) {
    throw new Error('useChatbotContext must be used within ChatbotProvider');
  }
  return ctx;
};

export default ChatbotContext;
